import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Stack, styled, Typography } from '@mui/material'
import { type FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import { COLORS, ICONS, LinkUnstyled, unit } from '@hcr/ui'
import { isNotNull, isNotUndefined, isNull } from '@hcr/utils'

import { SENTRY_CONFIG } from '../../../configs'
import { useLastPath, useUserDetails } from '../../../contexts'
import { useAccountQuery, useRelogin } from '../../../hooks'
import { AccountSearchParamsKeys, LocaleNamespace, Path } from '../../../models'
import { to } from '../../../utils'
import { ButtonSignOut, LayoutNavigationBack, LayoutNavigationBackError500, Metadata } from '../../common'

import { AccountLoading } from './AccountLoading'
import { UpdateEmailVerificationExpired, UsefulLinks } from './common'
import { UpdateEmailStatusMessage } from './common/update-email/UpdateEmailStatusMessage'

export const Account: FC = () => {
  const { t } = useTranslation()
  const lastPath = useLastPath()
  const [searchParams] = useSearchParams()
  const nonceSearchParam = searchParams.get(AccountSearchParamsKeys.Nonce)
  const statusSearchParam = searchParams.get(AccountSearchParamsKeys.Status)

  const account = useAccountQuery()
  const userDetails = useUserDetails()

  useRelogin()

  if (isNull(nonceSearchParam) && statusSearchParam === 'expired') {
    return <UpdateEmailVerificationExpired />
  }

  if (userDetails.isError) {
    return <LayoutNavigationBackError500 to={lastPath.root} />
  }

  if (userDetails.isSuccess && isNotUndefined(userDetails.data)) {
    return (
      <LayoutNavigationBack to={lastPath.root} arrowVariant='text'>
        <Metadata title={t('page-title.account')} />
        <Header>
          <Typography component='h1' className={SENTRY_CONFIG.privacy.className} variant='headlineS'>
            {userDetails.data.first_name} {userDetails.data.last_name}
          </Typography>
          <Typography className={SENTRY_CONFIG.privacy.className} variant='bodyM'>
            {userDetails.data.street_address}
          </Typography>
          {isNotNull(userDetails.data.street_address2) && (
            <Typography className={SENTRY_CONFIG.privacy.className} variant='bodyM'>
              {userDetails.data.street_address2}
            </Typography>
          )}
          <Typography className={SENTRY_CONFIG.privacy.className} variant='bodyM'>
            {userDetails.data.postal_code} {userDetails.data.city}
          </Typography>
          <Typography className={SENTRY_CONFIG.privacy.className} variant='bodyM'>
            {t(userDetails.data.country, { ns: LocaleNamespace.Country })}
          </Typography>
        </Header>
        <ListButton component={LinkUnstyled} to={to(Path.AccountEdit)}>
          <ListItemWithIcon>
            <Typography className={SENTRY_CONFIG.privacy.className} variant='labelM'>
              {t('account.email')}
            </Typography>
            <FontAwesomeIcon icon={ICONS.farPencil} />
          </ListItemWithIcon>
          <Typography className={SENTRY_CONFIG.privacy.className} variant='bodyM'>
            {userDetails.data.email}
          </Typography>
          <Typography sx={{ marginTop: unit(2) }} className={SENTRY_CONFIG.privacy.className} variant='labelM'>
            {t('account.phone-number')}
          </Typography>
          <Typography className={SENTRY_CONFIG.privacy.className} variant='bodyM'>
            {userDetails.data.country_code} {userDetails.data.phone}
          </Typography>
        </ListButton>
        <ListButton component={LinkUnstyled} to={to(Path.History)}>
          <ListItemWithIcon>
            <Typography variant='labelM'>{t('account.booking-history')}</Typography>
            <FontAwesomeIcon icon={ICONS.farChevronRight} />
          </ListItemWithIcon>
        </ListButton>
        <UsefulLinks links={account.isSuccess ? account.data.links : []} />
        <Footer>
          <ButtonSignOut variant='outlined' color='black' />
        </Footer>

        <UpdateEmailStatusMessage />
      </LayoutNavigationBack>
    )
  }

  return <AccountLoading to={lastPath.root} />
}

const Header = styled(Stack)`
  gap: ${unit(2)};
  padding: ${unit(24)} ${unit(7)} ${unit(4)};
  word-break: break-word;
`

const ListButton = styled(Button)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${unit(4)} ${unit(7)};
  color: ${COLORS.black};
  border-top: ${unit(0.25)} solid ${COLORS.vaalea[600]};
  word-break: break-word;
  font-weight: normal;
` as typeof Button

const ListItemWithIcon = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const Footer = styled(Box)`
  padding: ${unit(5)} ${unit(7)};
`
